import {
  HHButton,
  HHPaper,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FormProvider,
  Resolver,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import {
  ActiveStatus,
  fieldHasErrors,
  getDefaultPackageStartDate,
  getFormErrorsForContractField,
  getResetPackageFormValues,
  parsePackageFormValues,
} from '../../../utils/contract-utils';
import LoadingComponent from '../../utils/loading';
import { BillingTypeSelect } from './components/billing-type-select';
import { CurrencySelect } from './components/currency-select';
import { ContractDatePickers } from './components/date-pickers';
import FormPanelDetails from './components/form-right-panel';
import { GlobalDailyEngagementCap } from './components/global-daily-engagement-cap';
import {
  PackageFormConfirmation,
  PackageFormConfirmationProps,
} from './components/package-form-confirmation';
import { PartnershipSelect } from './components/partnership-select';
import { UserAnnualCap } from './components/user-annual-cap';
import {
  ContractPackageFormProps,
  DEFAULT_FORM_VALUES,
  DEFAULT_PACKAGE_FORM_VALUES,
  RawContractPackageFormValues,
} from './custom-package-types';
import { PackageForm } from './package-form';
import { useGetDefaultPackageFormValues } from './use-get-default-package-values';
import { useSubscriptionImpactCheck } from './use-subscription-impact-check';
import { PackageValidationSchema } from './validation-schema';
export const fieldArrayName = 'selectedPackages';

export enum ContractPackageFormLabelText {
  START_DATE = 'Start date',
  END_DATE = 'End date',
  USER_ANNUAL_CAP = 'User Annual spending cap',
  PACKAGE = 'Product Package',
  BILLING_MODEL = 'Billing Model',
  PRICE = 'Price',
  VOID = 'Void Contract',
  INITIAL_FEE = 'Initial Fee',
  ACTIVITY_FEE = 'Activity Fee',
  CURRENCY = 'Currency code',
  GLOBAL_DAILY_ENGAGEMENT_CAP = 'Global Daily Engagement Cap',
  BILLABLE_ACTIVITY = 'Billable Activities', //this matches to pre-existing label
}

export const ContractPackageForm = ({
  currentContracts,
  activeStatus = ActiveStatus.Unknown,
  editId,
  hideVoid,
  submitTitle,
  disableSubmit,
  partnershipId,
  onSubmitCallback,
  onCancel,
  wholeFormDisabled = false,
  startDateDisablePast = false,
  isBatchCreate = false,
  billingType,
  discounts,
}: ContractPackageFormProps): JSX.Element => {
  const memoizedCurrentContracts = useMemo(
    () => currentContracts,
    [currentContracts],
  );
  const location = useLocation();
  const {
    data: formData,
    isLoading,
    error,
  } = useGetDefaultPackageFormValues(memoizedCurrentContracts);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const serverUserAnnualCap = memoizedCurrentContracts.find(
    c => c.id === editId,
  )?.userAnnualCap;

  const methods = useForm<RawContractPackageFormValues>({
    mode: 'onChange',
    defaultValues: DEFAULT_FORM_VALUES,
    context: {
      startDateDisablePast,
      editId,
      currentContracts: memoizedCurrentContracts,
      billingModels: formData?.billingModels ?? [],
      availablePackages: formData?.availablePackages,
      engagementTypes: formData?.engagementTypes,
      serverUserAnnualCap,
    },
    resolver: yupResolver(
      PackageValidationSchema,
    ) as Resolver<RawContractPackageFormValues>,
    criteriaMode: 'all',
  });

  const {
    formState,
    handleSubmit,
    control,
    getValues,
    setValue,
    reset,
    trigger,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldArrayName,
  });

  const { isDirty, errors, isValid, isSubmitting, isSubmitted } = formState;

  const [
    watchedVoid,
    watchedCurrency,
    watchedSelectedPackages,
    watchedStartDate,
    watchedEndDate,
  ] = useWatch({
    control,
    name: ['isVoid', 'currency', 'selectedPackages', 'startDate', 'endDate'],
  });

  const watchedBillingModelIds = useMemo(
    () =>
      watchedSelectedPackages?.map(
        selectedPackage => selectedPackage.billingModelId,
      ) ?? [],
    [watchedSelectedPackages],
  );
  const previousBillingModelIds = useRef(watchedBillingModelIds);
  const previousContractStartDate = useRef<Date | null>(null);

  useEffect(() => {
    if (
      watchedBillingModelIds !== previousBillingModelIds.current &&
      watchedStartDate
    ) {
      trigger('startDate');
    }
    previousBillingModelIds.current = watchedBillingModelIds;
  }, [watchedBillingModelIds, watchedStartDate, trigger]);

  const onAction: PackageFormConfirmationProps['onAction'] = action => {
    setIsModalOpen(false);
    if (action === 'submit') {
      handleSubmit((data: RawContractPackageFormValues): void => {
        onSubmitCallback(
          parsePackageFormValues(data, formData?.billingModels ?? []),
        );
      })();
    }
    if (action === 'void') {
      handleSubmit((data: RawContractPackageFormValues): void => {
        onSubmitCallback(
          parsePackageFormValues(
            { ...data, isVoid: true },
            formData?.billingModels ?? [],
          ),
        );
      })();
    }
  };

  useEffect(() => {
    if (formData) {
      const contractToEdit = memoizedCurrentContracts.find(
        c => c.id === editId,
      );
      reset(
        getResetPackageFormValues(
          formData.billingModels,
          formData.templates,
          contractToEdit,
          partnershipId,
          billingType,
        ),
      );
    }
  }, [
    memoizedCurrentContracts,
    editId,
    formData,
    partnershipId,
    reset,
    billingType,
  ]);

  useEffect(() => {
    if (location.state?.contractContext?.endDate) {
      setTimeout(() => {
        setValue('endDate', location.state.contractContext.endDate, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }, 0);
    }
  }, [setValue, location?.state?.contractContext?.endDate]);

  useEffect(() => {
    if (!watchedStartDate || !watchedSelectedPackages) {
      return;
    }
    const seed = getDefaultPackageStartDate(watchedStartDate);
    watchedSelectedPackages.forEach((pkg, index) => {
      if (pkg.id == null && pkg.startDate == null) {
        setValue(`${fieldArrayName}.${index}.startDate`, seed, {
          shouldDirty: false,
          shouldValidate: true,
        });
      }
    });
  }, [watchedStartDate, watchedSelectedPackages, setValue]);

  useEffect(() => {
    const prevStart = previousContractStartDate.current;
    previousContractStartDate.current = watchedStartDate ?? null;

    if (
      !watchedStartDate ||
      !watchedSelectedPackages ||
      prevStart === null ||
      dayjs(watchedStartDate).isSame(dayjs(prevStart), 'day')
    ) {
      return;
    }

    const newStart = dayjs(watchedStartDate);
    const oldStart = dayjs(prevStart);

    watchedSelectedPackages.forEach((pkg, index) => {
      // null-start packages are seeded by the effect above;
      // reconcile the rest, whether saved (edit) or new (create).
      if (pkg.startDate == null) {
        return;
      }
      const pkgStart = dayjs(pkg.startDate);
      if (
        pkgStart.isSame(oldStart, 'day') ||
        pkgStart.isBefore(newStart, 'day')
      ) {
        setValue(`${fieldArrayName}.${index}.startDate`, watchedStartDate, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    });
  }, [watchedStartDate, watchedSelectedPackages, setValue]);

  const { subscriptionImpactError, subscriptionCheckPending } =
    useSubscriptionImpactCheck({
      activeStatus,
      editId,
      originalStartDate: memoizedCurrentContracts.find(c => c.id === editId)
        ?.startDate,
      watchedStartDate,
    });

  const isAddPackageDisabled =
    wholeFormDisabled && activeStatus !== ActiveStatus.Active;

  if (isLoading) {
    return <LoadingComponent center />;
  }

  if (error) {
    return (
      <HHTypography hhVariant="default">
        There was an error loading the form
      </HHTypography>
    );
  }

  return (
    <FormProvider {...methods}>
      <PackageFormConfirmation
        formValues={parsePackageFormValues(
          getValues(),
          formData?.billingModels ?? [],
        )}
        availablePackages={formData?.availablePackages ?? []}
        billingModels={formData?.billingModels ?? []}
        onAction={onAction}
        open={isModalOpen}
        isVoid={watchedVoid}
        editId={editId}
        confirmText={watchedVoid ? 'Void' : 'Confirm'}
        isBatchCreate={isBatchCreate}
      />
      <form>
        <HHStack
          direction="column"
          spacing={4}
          padding={4}
          data-testid={'contract-package-form-test-id'}
        >
          <HHStack direction="row" spacing={2}>
            <PartnershipSelect wholeFormDisabled={wholeFormDisabled} />
            <BillingTypeSelect name="billingType" label="Billing Type" />
          </HHStack>
          <ContractDatePickers
            startDateDisablePast={startDateDisablePast}
            startlabel={ContractPackageFormLabelText.START_DATE}
            endLabel={ContractPackageFormLabelText.END_DATE}
            defaultStartDateCalenderDate={
              formData?.defaultStartDateCalenderDate ?? dayjs()
            }
            wholeFormDisabled={wholeFormDisabled}
            currentContracts={memoizedCurrentContracts}
            contractEditId={editId}
            submissionCount={0}
            activeStatus={activeStatus}
            subscriptionImpactError={subscriptionImpactError}
          />
          <HHStack direction="row" spacing={2}>
            <HHStack direction="row" spacing={2} flex={1}>
              <CurrencySelect wholeFormDisabled={wholeFormDisabled} />
              <UserAnnualCap
                wholeFormDisabled={wholeFormDisabled}
                billingModels={formData?.billingModels ?? []}
              />
            </HHStack>
            <HHStack flex={1}>
              <GlobalDailyEngagementCap wholeFormDisabled={wholeFormDisabled} />
            </HHStack>
          </HHStack>
          {fields.map((field, index) => (
            <HHStack direction="row" spacing={4} key={field.id} flex={1}>
              <PackageForm
                index={index}
                availablePackages={formData?.availablePackages ?? []}
                billingModels={formData?.billingModels ?? []}
                engagementTypes={formData?.engagementTypes ?? []}
                templates={formData?.templates ?? []}
                wholeFormDisabled={
                  watchedSelectedPackages[index]?.id
                    ? wholeFormDisabled
                    : wholeFormDisabled && activeStatus !== ActiveStatus.Active
                }
                packageRules={formData?.packageRules ?? { billingModels: {} }}
                value={field}
                key={field.id}
                remove={remove}
                existingContract={memoizedCurrentContracts?.find(
                  c => c.id === editId,
                )}
                costShareSetupOptions={
                  formData?.costShareSetupOptions ?? {
                    cadence: [],
                    deductionTiming: [],
                  }
                }
              />
              {watchedSelectedPackages?.some(Boolean) &&
                watchedSelectedPackages[index]?.packageId != null && (
                  <FormPanelDetails
                    sx={{ flex: 1, alignItems: 'stretch', minHeight: '100%' }}
                    key={index}
                    selectedPackage={watchedSelectedPackages[index]}
                    availablePackages={formData?.availablePackages ?? []}
                    engagementTypes={formData?.engagementTypes ?? []}
                    currency={watchedCurrency.value}
                    billingModels={formData?.billingModels ?? []}
                    templates={formData?.templates ?? []}
                    currentContract={memoizedCurrentContracts?.find(
                      c => c.id === editId,
                    )}
                    discounts={discounts}
                    contractStartDate={watchedStartDate}
                    contractEndDate={watchedEndDate}
                  />
                )}
            </HHStack>
          ))}
          <button
            onClick={(e): void => {
              e.preventDefault();
              if (isAddPackageDisabled) {
                return;
              }
              append({
                ...DEFAULT_PACKAGE_FORM_VALUES,
                startDate: getDefaultPackageStartDate(watchedStartDate),
              });
            }}
            disabled={isAddPackageDisabled}
            name="addPackage"
            style={{
              width: '100%',
              padding: 0,
              border: 'none',
              background: 'none',
              cursor: isAddPackageDisabled ? 'default' : 'pointer',
            }}
          >
            <HHPaper
              sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'background-color 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: isAddPackageDisabled
                    ? 'inherit'
                    : 'action.hover',
                },
                '&:active': {
                  backgroundColor: isAddPackageDisabled
                    ? 'inherit'
                    : 'action.selected',
                  outline: isAddPackageDisabled ? 'none' : '2px solid',
                  outlineColor: isAddPackageDisabled
                    ? 'transparent'
                    : 'primary.main',
                },
              }}
            >
              <HHStack direction="row" alignItems="center" padding={4}>
                <AddIcon color="primary" />
                <HHTypography hhVariant="default">Add Package</HHTypography>
              </HHStack>
            </HHPaper>
          </button>
          <HHStack
            direction="row"
            spacing={2}
            justifyContent={hideVoid ? 'flex-end' : 'space-between'}
          >
            {hideVoid ? null : (
              <HHButton
                data-testid="contract-form-void-button"
                name="isVoid"
                hhVariant="variant-bypass"
                variant="outlined"
                type="button"
                color="error"
                onClick={(): void => {
                  setValue('isVoid', true);
                  setIsModalOpen(true);
                }}
              >
                {ContractPackageFormLabelText.VOID}
              </HHButton>
            )}
            <HHStack direction="row" spacing={2}>
              {fieldHasErrors('selectedPackages', errors) && (
                <HHStack alignItems="center" justifyContent="center">
                  <HHTypography hhVariant="default" color="error">
                    {getFormErrorsForContractField('selectedPackages', errors)}
                  </HHTypography>
                </HHStack>
              )}
              <HHButton
                data-testid="contract-form-cancel-button"
                hhVariant="variant-bypass"
                variant="outlined"
                size="medium"
                color="primary"
                onClick={onCancel}
              >
                Cancel
              </HHButton>
              <HHButton
                data-testid="contract-form-submit-button"
                hhVariant="variant-bypass"
                variant="contained"
                size="medium"
                color="primary"
                disabled={
                  disableSubmit ||
                  !isDirty ||
                  (!!errors && !isValid) ||
                  isSubmitting ||
                  isSubmitted ||
                  subscriptionCheckPending ||
                  !!subscriptionImpactError
                }
                onClick={(): void => {
                  setValue('isVoid', false);
                  setIsModalOpen(true);
                }}
              >
                {submitTitle}
              </HHButton>
            </HHStack>
          </HHStack>
        </HHStack>
      </form>
    </FormProvider>
  );
};
